import { FC, useEffect, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Slider from '@mui/material/Slider'
import CircularProgress from '@mui/material/CircularProgress'
import { AiCallNodeData, WorkflowNodeData } from '../types'
import { themeColor } from '../../../layout/theme/color'
import { getJson, getUrl } from '../../../providers/data'
import ExpressionTextField from '../autocomplete/ExpressionTextField'
import ExpressionInsertPill from '../components/ExpressionInsertPill'
import OutputExampleField from './OutputExampleField'

interface AvailableModel {
  model_id: string
  model_name: string
  provider: string
}

interface AiCallConfigProps {
  data: AiCallNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
}

const PromptField: FC<{
  label: string
  value: string
  placeholder: string
  minRows: number
  onChange: (value: string) => void
}> = ({ label, value, placeholder, minRows, onChange }) => {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  const handleInsertBraces = () => {
    const el = inputRef.current
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? start
    const next = `${value.slice(0, start)}{{  }}${value.slice(end)}`
    onChange(next)
    requestAnimationFrame(() => {
      const field = inputRef.current
      if (field) {
        field.focus()
        field.setSelectionRange(start + 3, start + 3)
      }
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <ExpressionTextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        multiline
        minRows={minRows}
        placeholder={placeholder}
        inputRef={(el: HTMLInputElement | HTMLTextAreaElement | null) => {
          inputRef.current = el
        }}
        InputProps={{
          sx: { '& .MuiInputBase-inputMultiline': { resize: 'vertical' } },
        }}
      />
      <Box sx={{ display: 'flex', px: 0.25 }}>
        <ExpressionInsertPill onInsert={handleInsertBraces} />
      </Box>
    </Box>
  )
}

const AiCallConfig: FC<AiCallConfigProps> = ({ data, onUpdate }) => {
  const translate = useTranslate()
  const [models, setModels] = useState<AvailableModel[]>([])
  const [modelsLoading, setModelsLoading] = useState(true)

  useEffect(() => {
    getJson<AvailableModel[]>(`${getUrl()}/workflow_execution/available-models`)
      .then(setModels)
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false))
  }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      <TextField
        select
        label={translate('workflow.config.aiModel')}
        value={data.model}
        onChange={(e) => onUpdate({ model: e.target.value })}
        size="small"
        fullWidth
        disabled={modelsLoading}
        InputProps={{
          endAdornment: modelsLoading ? <CircularProgress size={16} /> : undefined,
        }}
      >
        {models.map((m) => (
          <MenuItem key={m.model_id} value={m.model_id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{m.model_name}</Typography>
              <Typography variant="caption" sx={{ color: themeColor.slate50 }}>
                {m.provider}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <PromptField
        label={translate('workflow.config.systemPrompt')}
        value={data.systemPrompt}
        onChange={(value) => onUpdate({ systemPrompt: value })}
        minRows={3}
        placeholder={translate('workflow.config.systemPromptPlaceholder')}
      />

      <PromptField
        label={translate('workflow.config.userPrompt')}
        value={data.userPrompt}
        onChange={(value) => onUpdate({ userPrompt: value })}
        minRows={5}
        placeholder={translate('workflow.config.userPromptPlaceholder')}
      />

      <FormControlLabel
        control={
          <Switch
            checked={data.expectJson}
            onChange={(e) => onUpdate({ expectJson: e.target.checked })}
            size="small"
          />
        }
        label={
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            {translate('workflow.config.expectJson')}
          </Typography>
        }
      />

      {data.expectJson && (
        <OutputExampleField
          value={data.outputExample}
          onChange={(outputExample) => onUpdate({ outputExample })}
        />
      )}

      <Box>
        <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: themeColor.slate60, mb: 0.5 }}>
          {translate('workflow.config.temperature')}: {data.temperature}
        </Typography>
        <Slider
          value={data.temperature}
          onChange={(_, v) => onUpdate({ temperature: v as number })}
          min={0}
          max={1}
          step={0.1}
          size="small"
          sx={{ color: '#6366F1' }}
        />
      </Box>
    </Box>
  )
}

export default AiCallConfig
